import { Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    DataMaintenanceLocationP58Model,
    DataMaintenanceModel,
    DataMaintenanceSystemConfigP58Model,
    MaterialOrderModel,
    MaterialQuoteModel,
    MaterialService,
    ModalClass,
    NotificationModel,
    UserMasterService,
} from '@evorait/pace-core';
import { FormsState, getDocBox } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MaterialOrderComponent } from '../../material/material-order/material-order.component';
import { MaterialPricingComponent } from '../../material/material-pricing/material-pricing.component';
import { MaterialPricingModel } from '../../material/material-pricing/material-pricing.model';
import { MaterialQuoteComponent } from '../../material/material-quote/material-quote.component';
import { LocalStorageSalesQuoteService } from '../../material/services/local-storage-sales-quote.service';
import { DataMaintenanceLocationComponent } from '../data-maintenance/data-maintenance-location.component';
import { DataMaintenanceSysconfigComponent } from '../data-maintenance/data-maintenance-sysconfig.component';
import { EquipmentConfigurationComponent } from '../equipment-configuration/equipment-configuration.component';
import EquipmentConfigurationModel from '../equipment-configuration/equipment-configuration.model';
import { EquipmentHistoryComponent } from '../equipment-history/equipment-history.component';
import EquipmentHistoryModel from '../equipment-history/equipment-history.model';
import { finalize, take } from 'rxjs/operators';
import { EquipmentOtherTasksComponent } from '../equipment-other-tasks/equipment-other-tasks.component';

const MAX_NUM_OF_LINKS_PER_COL = 3;

@Component({
    selector: 'pace-actions',
    templateUrl: './actions.component.html',
    styleUrls: ['./actions.component.scss'],
})
export class ActionsComponent implements OnChanges {
    @Input()
    public notification: NotificationModel;

    public displayMaterialPricing: boolean;
    public displayMaterialQuote: boolean;
    public displayDataMaintenanceSection: boolean;
    public displayEquipmentHistory: boolean;
    public displayMaterialSection: boolean;
    public displayLinksSection: boolean;
    private readonly teamplayFleetLink: string;
    public teamPlayFleetLinkForNotification = '';
    private readonly serviceSoftwareOnlineLink: string;
    public serviceSoftwareOnlineLinkForNotification = '';
    private readonly serviceSoftwareOnlineDivisions: string[];
    public readonly displayDocBox: boolean;
    public readonly byBoxKonnectLink: string;
    private readonly cbDocLink: string;
    public cbDocOnlineLink: string;
    public displayLinksInMultiColumns = false;
    public displayOtherTasks: boolean;
    private isOpeningMaterialRequest = false;

    public constructor(
        private readonly modalService: BsModalService,
        private readonly configService: ConfigService,
        private readonly materialService: MaterialService,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly store: Store<FormsState>,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly userMasterService: UserMasterService,
    ) {
        this.displayDataMaintenanceSection = !this.clientManager.isPaceUS();
        this.displayEquipmentHistory = !this.clientManager.isPaceUS();
        this.displayOtherTasks = this.configService.getEnableOtherTasks() && !this.userMasterService.isPaceOnlineUser();
        this.displayMaterialSection = !this.clientManager.isPaceUS();
        this.teamplayFleetLink = this.clientManager.isNgmP58() ? this.configService.getTeamplayFleetLink() : '';
        this.serviceSoftwareOnlineLink = this.configService.getSeSoLink();
        this.serviceSoftwareOnlineDivisions = this.configService.getSeSoDivisions();
        this.displayDocBox = this.configService.getDisplayDocBox();
        this.byBoxKonnectLink = this.configService.getByBoxKonnectLink();
        this.cbDocLink = this.configService.getCBDocLink();
    }

    public initData(): void {
        const isNgmP58AustriaInvitro =
            this.clientManager.isNgmP58Austria() &&
            this.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;

        // additional check for isNgmP58AustriaInvitro required as the salesOrg/Plant is same for both invivo and
        // invitro processes in Austria and material pricing should be displayed only for Austria inVivo notifications
        this.displayMaterialPricing = this.configService.getDisplayMaterialPricing() && !isNgmP58AustriaInvitro;

        // For Finland, Material quote should be displayed only for inVitro notifications
        const isNGMP58FinlandInvitro =
            this.clientManager.isNgmP58Finland() &&
            this.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;

        this.displayMaterialQuote =
            (this.configService.getDisplayMaterialQuote() || isNGMP58FinlandInvitro) &&
            !this.configService.getDisableQuoteForNotificationTypes(this.notification.notificationType);

        this.serviceSoftwareOnlineLinkForNotification = '';
        this.cbDocOnlineLink = '';

        if (
            this.teamplayFleetLink.length > 0 ||
            this.serviceSoftwareOnlineLink.length > 0 ||
            this.cbDocLink.length > 0
        ) {
            const mainEquipment = this.notification.getMainEquipment();
            if (typeof mainEquipment !== 'undefined') {
                const matnr = mainEquipment.matnr.value;
                const sernr = mainEquipment.sernr.value;
                const lengthOfCountry = 2;
                const country = this.notification.salesOrg.substring(0, lengthOfCountry);
                const division = mainEquipment.division.value;
                if (this.teamplayFleetLink.length > 0) {
                    const params = `?materialNo=${matnr}&serialNo=${sernr}&country=${country}`;
                    this.teamPlayFleetLinkForNotification = this.teamplayFleetLink.concat(params);
                }
                if (
                    this.serviceSoftwareOnlineLink.length > 0 &&
                    this.serviceSoftwareOnlineDivisions.includes(division)
                ) {
                    const params = `${matnr}/${sernr}`;
                    this.serviceSoftwareOnlineLinkForNotification = this.serviceSoftwareOnlineLink.concat(params);
                }
                if (this.cbDocLink.length > 0) {
                    if (!this.cbDocLink.includes('materialNumber')) {
                        this.log.error(
                            `ActionsComponent: CB-DOC link : ERROR : 'materialNumber' string not found in cbDocLink - ${this.cbDocLink}`,
                        );
                    } else this.cbDocOnlineLink = this.cbDocLink.replace('materialNumber', matnr);
                }
            }
        }

        const linkstoBeDisplayed = [
            this.teamPlayFleetLinkForNotification.length > 0,
            this.serviceSoftwareOnlineLinkForNotification.length > 0,
            this.displayDocBox,
            this.byBoxKonnectLink.length > 0,
            this.cbDocLink.length > 0,
        ].filter(Boolean);

        this.displayLinksSection = !!linkstoBeDisplayed.length;

        this.displayLinksInMultiColumns = linkstoBeDisplayed.length > MAX_NUM_OF_LINKS_PER_COL;
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.notification.currentValue) {
            this.notification = <NotificationModel>changesObj.notification.currentValue;
            this.initData();
        }
    }

    public openEquipmentConfig(): void {
        this.modalService.show(EquipmentConfigurationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new EquipmentConfigurationModel(this.notification),
                notification: this.notification,
            },
            ariaLabelledBy: 'equipment-configuration-modal-title',
        });
    }

    public openEquipmentHistory(): void {
        this.modalService.show(EquipmentHistoryComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new EquipmentHistoryModel(this.notification),
            },
            ariaLabelledBy: 'equipment-history-modal-title',
        });
    }

    public openEquipmentOtherTasks(): void {
        this.modalService.show(EquipmentOtherTasksComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                notification: this.notification,
            },
        });
    }

    public openNotificationMaintenanceSysConfig(): void {
        this.modalService.show(DataMaintenanceSysconfigComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: this.clientManager.isNgmP58()
                    ? new DataMaintenanceSystemConfigP58Model(this.notification)
                    : new DataMaintenanceModel(this.notification),
            },
            ariaLabelledBy: 'data-maintenance-system-config-modal-title',
        });
    }

    public openNotificationMaintenanceLocation(): void {
        this.modalService.show(DataMaintenanceLocationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: this.clientManager.isNgmP58()
                    ? new DataMaintenanceLocationP58Model(this.notification)
                    : new DataMaintenanceModel(this.notification),
            },
            ariaLabelledBy: 'data-maintenance-location-modal-title',
        });
    }

    public openNotificationMaterialRequest(): void {
        if (this.isOpeningMaterialRequest) return;

        this.isOpeningMaterialRequest = true;

        this.materialService
            .getSavedMaterialOrderModel(this.notification.identifier)
            .pipe(
                take(1),
                finalize(() => (this.isOpeningMaterialRequest = false)),
            )
            .subscribe((savedMaterialOrderModel) => {
                if (!savedMaterialOrderModel?.isSubmitted) {
                    this.modalService.show(MaterialOrderComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        initialState: {
                            model: new MaterialOrderModel(this.notification, undefined, savedMaterialOrderModel),
                            displayMaterialPricing: this.displayMaterialPricing,
                            displayMaterialQuote: this.displayMaterialQuote,
                        },
                        ariaLabelledBy: 'material-order-modal-title',
                    });
                }
            });
    }

    // check if material is already requested for this notification
    public isMaterialRequested(): boolean {
        return this.materialService.isMaterialRequested(this.notification.identifier);
    }

    public openNotificationSalesQuote(): void {
        const localSalesQuote = this.localStorageSalesQuoteService.getSalesQuote(this.notification.identifier);

        this.modalService.show(MaterialQuoteComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: localSalesQuote ? localSalesQuote : new MaterialQuoteModel(undefined, this.notification),
            },
            ariaLabelledBy: 'material-quote-modal-title',
        });
    }

    public openNotificationMaterialPricing(): void {
        this.modalService.show(MaterialPricingComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialPricingModel(this.notification),
                displayMaterialQuote: this.displayMaterialQuote,
            },
            ariaLabelledBy: 'material-pricing-modal-title',
        });
    }

    public callDocBoxPage(): void {
        const mainEquipment = this.notification.getMainEquipment();

        if (mainEquipment) {
            this.store.dispatch(
                getDocBox({
                    notificationId: this.notification.getNotificationState().identifier,
                    materialNumber: mainEquipment.matnr.value,
                    serialNumber: mainEquipment.sernr.value,
                }),
            );
        }
    }
}
